import { useState } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import {
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    Clock,
    Monitor,
} from "lucide-react";
import { Badge } from "../../../ui/common-ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { Button } from "../../../ui/common-ui/button";
import { NGROK } from "../../../../APIs";

interface GitlabRecentActivityProps {
    tenantName: string;
    instanceUrl: string;
    endpointType: string;
    entityValue: string;
}

function formatTimeAgo(timestamp: string | null | undefined): string {
    if (!timestamp) return "-";
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return String(timestamp);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hrs ago`;
    const days = Math.floor(hrs / 24);
    return `${days} days ago`;
}

export default function GitlabRecentActivity({
    tenantName,
    instanceUrl,
    endpointType,
    entityValue,
}: GitlabRecentActivityProps) {
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedLogs, setExpandedLogs] = useState<Set<number>>(new Set());
    const itemsPerPage = 10;

    const {
        data: activity = [],
        isLoading,
    } = useQuery({
        queryKey: ["gitlab-recent-activity", tenantName, instanceUrl, endpointType, entityValue],
        queryFn: async () => {
            const { data } = await axios.get(
                `${NGROK}/api/saas/gitlab/data/entity`,
                {
                    params: {
                        tenantName,
                        instanceUrl,
                        entity: entityValue,
                        entityType: endpointType,
                    },
                }
            );
            return Array.isArray(data) ? data : [];
        },
        enabled: !!tenantName && !!instanceUrl && !!entityValue,
    });

    const totalPages = Math.ceil(activity.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedLogs = activity.slice(startIndex, startIndex + itemsPerPage);

    const toggleLog = (id: number) => {
        const next = new Set(expandedLogs);
        next.has(id) ? next.delete(id) : next.add(id);
        setExpandedLogs(next);
    };

    const getSeverityBadge = (level: string = "info") => {
        const lower = (level || "info").toLowerCase();
        switch (lower) {
            case "critical":
                return <Badge className="text-xs text-white bg-red-600 hover:bg-red-700">{level}</Badge>;
            case "high":
            case "notice":
                return <Badge className="text-xs text-white bg-orange-500 hover:bg-orange-600">{level}</Badge>;
            case "medium":
            case "warn":
                return <Badge className="text-xs text-white bg-yellow-500 hover:bg-yellow-600">{level}</Badge>;
            case "low":
            case "info":
                return <Badge className="text-xs text-white bg-blue-500 hover:bg-blue-600">{level}</Badge>;
            default:
                return <Badge variant="secondary" className="text-xs">INFO</Badge>;
        }
    };

    const getPageNumbers = (current: number, total: number) => {
        const delta = 2;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let last: number | null = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                range.push(i);
            }
        }
        for (const i of range) {
            if (last) {
                if ((i as number) - last === 2) rangeWithDots.push(last + 1);
                else if ((i as number) - last !== 1) rangeWithDots.push("…");
            }
            rangeWithDots.push(i);
            last = i as number;
        }
        return rangeWithDots;
    };

    const renderSkeletonRow = (key: number) => (
        <div
            key={key}
            className="grid gap-4 px-3 py-3 text-sm animate-pulse"
            style={{ gridTemplateColumns: "100px 2fr 2fr 1fr" }}
        >
            <div className="w-20 h-6 bg-muted rounded" />
            <div className="w-full h-6 bg-muted rounded" />
            <div className="w-full h-6 bg-muted rounded" />
            <div className="w-16 h-6 bg-muted rounded" />
        </div>
    );

    return (
        <Card className="portal-card-elevated">
            <CardHeader>
                <CardTitle>Recent GitLab Activities</CardTitle>
            </CardHeader>

            <CardContent>
                <div className="space-y-4">
                    {/* Table Header */}
                    <div
                        className="grid gap-4 px-3 py-2.5 text-[11px] font-semibold tracking-wide uppercase rounded-[10px] border border-border shadow-[0_2px_8px_rgb(15_23_42/0.06)] text-muted-foreground"
                        style={{ gridTemplateColumns: "90px 2fr 1.5fr 1.5fr 1fr" }}
                    >
                        <div>Level</div>
                        <div>Details</div>
                        <div>Action</div>
                        <div>Resource</div>
                        <div>Source</div>
                    </div>

                    {/* Loading */}
                    {isLoading && (
                        <div className="space-y-1">
                            {[...Array(itemsPerPage)].map((_, idx) => renderSkeletonRow(idx))}
                        </div>
                    )}

                    {/* Empty */}
                    {!isLoading && activity.length === 0 && (
                        <div className="py-8 text-sm text-center text-muted-foreground">
                            No activity logs found.
                        </div>
                    )}

                    {/* Rows */}
                    {!isLoading &&
                        paginatedLogs.map((log: any, idx: number) => {
                            const logId = log.id || idx;
                            const isExpanded = expandedLogs.has(logId);

                            return (
                                <div
                                    key={logId}
                                    className="border rounded-md border-border portal-card-hover"
                                >
                                    <div
                                        className="grid gap-4 px-3 py-3 text-sm cursor-pointer"
                                        style={{ gridTemplateColumns: "90px 2fr 1.5fr 1.5fr 1fr" }}
                                        onClick={() => toggleLog(logId)}
                                    >
                                        {/* Level */}
                                        <div className="flex items-center gap-2">
                                            {isExpanded ? (
                                                <ChevronUp className="w-4 h-4" />
                                            ) : (
                                                <ChevronDown className="w-4 h-4" />
                                            )}
                                            {getSeverityBadge(log.logType || log.riskLevel || log.level)}
                                        </div>

                                        {/* Details */}
                                        <div className="min-w-0 space-y-1">
                                            <div className="flex items-center gap-1 text-xs text-foreground">
                                                <Monitor className="w-3 h-3 text-muted-foreground" />
                                                <span className="truncate" title={log.entity || log.entityAssociated}>
                                                    {log.entity || log.entityAssociated || "-"}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                <Clock className="w-3 h-3" />
                                                <span>{formatTimeAgo(log.logTimestamp || log.timestamp)}</span>
                                            </div>
                                        </div>

                                        {/* Action */}
                                        <div className="space-y-1">
                                            <div className="font-semibold text-foreground text-xs break-all whitespace-pre-wrap" title={log.action}>
                                                {log.action || "-"}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground" title={log.category}>
                                                {log.category || ""}
                                            </div>
                                        </div>

                                        {/* Resource */}
                                        <div>
                                            <div className="text-xs text-foreground" title={log.resource || log.entityAssociated}>
                                                {log.resource || log.entityAssociated || "—"}
                                            </div>
                                        </div>

                                        {/* Source */}
                                        <div>
                                            <div className="text-xs text-foreground" title={log.sourceIp || log.source}>
                                                {log.sourceIp || log.source || "—"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded Raw Log */}
                                    {isExpanded && log.rawLog && (
                                        <div className="border-t px-4 py-3 bg-muted/30">
                                            <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all max-h-64 overflow-auto">
                                                {typeof log.rawLog === "string"
                                                    ? log.rawLog
                                                    : JSON.stringify(log.rawLog, null, 2)}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                    {/* Pagination */}
                    {!isLoading && activity.length > 0 && totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="p-2"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                {getPageNumbers(currentPage, totalPages).map((p, i) =>
                                    typeof p === "number" ? (
                                        <Button
                                            key={i}
                                            variant={currentPage === p ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setCurrentPage(p)}
                                            className="h-8 min-w-8"
                                        >
                                            {p}
                                        </Button>
                                    ) : (
                                        <span key={i} className="px-2 text-muted-foreground">…</span>
                                    )
                                )}
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="p-2"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}
